import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, CreditCard, LogOut, Loader2, Star, Zap, Crown, Check } from 'lucide-react';

const GY_GREEN = '#009b3a';
const GY_YELLOW = '#fedd00';

const PLAN_ICONS = { start: Star, pro: Zap, super: Crown };
const PLAN_FEATURES = {
  start: ['Gestion des réparations', 'Suivi client', 'Devis & Factures'],
  pro: ['Tout le plan Start', 'Multi-utilisateurs', 'Statistiques avancées'],
  super: ['Tout le plan Pro', 'Points de vente illimités', 'Support dédié 24/7'],
};

const TrialExpiredBlocker = () => {
  const { user, api, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('start');
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    if (!user || !['trial_expired', 'expired', 'suspended'].includes(user.company_status)) return;
    api.get('/stripe/plans').then(r => setPlans(r.data?.plans || null)).catch(() => {});
  }, [user, api]);

  if (!user || user.role !== 'admin' || !['trial_expired', 'expired', 'suspended'].includes(user.company_status)) {
    return null;
  }

  const handleSubscribe = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/stripe/create-checkout', {
        plan: selectedPlan,
        origin_url: window.location.origin
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création du paiement');
    } finally {
      setLoading(false);
    }
  };

  const displayPlans = plans || {
    start: { name: 'Start', price: 29.99 },
    pro: { name: 'Pro', price: 59.99 },
    super: { name: 'Super', price: 99.99 }
  };

  return (
    <div className="fixed inset-0 bg-[#050a14]/95 z-[90] flex items-center justify-center p-4 overflow-y-auto" data-testid="trial-expired-blocker">
      <div className="max-w-2xl w-full bg-[#111827] border border-white/10 rounded-2xl p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-red-500/10 border border-red-500/20">
            <AlertTriangle className="w-7 h-7 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            {user.company_status === 'trial_expired' ? 'Essai gratuit terminé' : 'Abonnement expiré'}
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed">
            {user.company_name ? `${user.company_name}, votre ` : 'Votre '}
            {user.company_status === 'trial_expired' ? "période d'essai est terminée." : 'abonnement a expiré.'}
            {' '}Choisissez un plan pour continuer à utiliser GUYAREPAIR.
          </p>
        </div>

        {/* Plan selection */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {Object.entries(displayPlans).map(([key, plan]) => {
            const Icon = PLAN_ICONS[key] || Star;
            const features = PLAN_FEATURES[key] || [];
            const isSelected = selectedPlan === key;
            return (
              <button
                key={key}
                onClick={() => setSelectedPlan(key)}
                className={`rounded-xl border p-4 text-left transition-all ${
                  isSelected
                    ? 'border-emerald-500/50 bg-emerald-500/10 ring-1 ring-emerald-500/30'
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                }`}
                data-testid={`blocker-plan-${key}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${isSelected ? 'text-emerald-400' : 'text-gray-500'}`} />
                  <span className="text-sm font-bold text-white">{plan.name}</span>
                </div>
                <p className="text-lg font-black text-white mb-2">{plan.price} <span className="text-xs text-gray-500 font-normal">EUR/mois</span></p>
                <ul className="space-y-1">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-center gap-1.5 text-[11px] text-gray-400">
                      <Check className="w-3 h-3 text-emerald-400 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400" data-testid="trial-blocker-error">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleSubscribe}
            disabled={loading}
            data-testid="trial-subscribe-btn"
            className="w-full py-3.5 rounded-xl font-bold text-sm text-[#050a14] disabled:opacity-50 flex items-center justify-center gap-2 transition-all hover:opacity-90"
            style={{ background: `linear-gradient(135deg, ${GY_GREEN}, ${GY_YELLOW})` }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Activer le plan {displayPlans[selectedPlan]?.name} - {displayPlans[selectedPlan]?.price} EUR/mois
          </button>
          <button
            onClick={() => { logout(); window.location.href = '/'; }}
            data-testid="trial-logout-btn"
            className="w-full py-3 rounded-xl text-sm text-gray-400 border border-white/10 hover:bg-white/5 flex items-center justify-center gap-2 transition-all"
          >
            <LogOut className="w-4 h-4" /> Se déconnecter
          </button>
        </div>

        <p className="text-xs text-gray-600 mt-4 text-center">
          Vos données sont conservées et seront accessibles après activation.
        </p>
      </div>
    </div>
  );
};

export default TrialExpiredBlocker;
